import { Injectable } from "@angular/core";
import { Observable, map, tap } from "rxjs";
import {
  andAllWhere,
  andWhere,
  buildQuery,
  CombinedFilter,
  noop,
  Ops,
  orderBy,
  OrderVariant,
  orWhere,
  page,
  seed,
  select,
  where,

} from "@albatrosdeveloper/ave-utils-npm/lib/utils/query.util";
import { HttpClient, HttpParams } from "@angular/common/http";
import { AbstractService } from "app/services/abstract.service";
import { environment as env } from "../../../../../../environments/environment.prod";
import { IAPIListResult, IPagination, IPaginationResult } from "app/common/interfaces";
import AccountStatementsAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/account-statements/account-statements.entity";
import { RequestOptions } from "app/common/interfaces/options.interface";
import { IFilters } from "app/modules/components/filters/interfaces/filterInterface";
import { buildSearchQueries } from "app/utils/query-search.util";
import { saveAs } from "file-saver";

export interface IBusinessPartnerAccountStatementsResult<T> {
    status: string;
    statusCode: string;
    data: {
        data: T[];
        pageInfo: Array<{ _id: null; total: number }>;
    };
}

const apiUrl = env.API_PAYMENT_MASTER_URL;
const moduleName = "account_statements";

@Injectable({
  providedIn: "root",
})
export class OpentItemsService extends AbstractService<AccountStatementsAttributes,AccountStatementsAttributes,AccountStatementsAttributes> {

  /** The last filter used to load the list */
  currentFilter: CombinedFilter<AccountStatementsAttributes>;
  currentFilterData: {search: string};

    constructor(
        http: HttpClient,
    ) {
        super(apiUrl, moduleName, http);
    }

    /**
     * Pagination
     * @param options Pagination options
     * @returns Paginated data
     * 
     * * 📍 Used in:
     * * - Finanzas/Partidas abiertas (`open-p-item.component.ts`)
     * * - Reportes/Finanzas/Partidas abiertas (`open-item.component.ts`)
     */
    findAllWithCount(
        options: IPagination<AccountStatementsAttributes>,
        dataToFilter: IFilters,
    ): Observable<IPaginationResult<AccountStatementsAttributes>> {
        // Build filter without pagination for export
        const filterWithoutPagination = buildQuery<AccountStatementsAttributes>(
            where('_deleted', Ops.eq(false)),
            andWhere("businessPartner.name" as keyof AccountStatementsAttributes, Ops.like(options.search ?? "", "i")),
            orWhere("businessPartner.externalKeys.code" as keyof AccountStatementsAttributes, Ops.like(options.search ?? "", "i")),
            orWhere("additionalOC", Ops.like(options.search ?? "", "i")),
            orWhere("document", Ops.like(options.search ?? "", "i")),
            orWhere("codeReason", Ops.like(options.search ?? "", "i")),
            dataToFilter.subRouteId && dataToFilter.subRouteId.length > 0 ? andAllWhere('subRoutes.id' as keyof AccountStatementsAttributes, Ops.in(...dataToFilter.subRouteId)) : noop(),
            dataToFilter.customerId  && dataToFilter.customerId.length > 0 ? andAllWhere('businessPartner.id' as keyof AccountStatementsAttributes, Ops.in(...dataToFilter.customerId)) : noop(),
            dataToFilter.salesOfficeId  && dataToFilter.salesOfficeId.length > 0 ? andAllWhere('salesOffice.id' as keyof AccountStatementsAttributes, Ops.in(...dataToFilter.salesOfficeId)) : noop(),
            dataToFilter.paymentConditionId  && dataToFilter.paymentConditionId.length > 0 ? andAllWhere('paymentConditionShort.id' as keyof AccountStatementsAttributes, Ops.in(...dataToFilter.paymentConditionId)) : noop(),
            dataToFilter.companyId ? andAllWhere('company.id' as keyof AccountStatementsAttributes, Ops.eq(dataToFilter.companyId)) : noop(),
            // dataToFilter.businessPartners && dataToFilter.businessPartners.length > 0 ? andAllWhere('businessPartner.id' as keyof AccountStatementsAttributes, Ops.in(...dataToFilter.businessPartners)) : noop(),
            dataToFilter.startDate && dataToFilter.endDate
                ? andAllWhere("creationDate", Ops.btwnDates(dataToFilter.startDate, dataToFilter.endDate))
                : noop(),
            dataToFilter.expirationStartDate && dataToFilter.expirationEndDate
                ? andAllWhere("expirationDate", Ops.btwnDates(dataToFilter.expirationStartDate, dataToFilter.expirationEndDate))
                : noop(),
            // date ? andAllWhere("creationDate", Ops.btwnDates(date, new Date())): noop(),
            orderBy(
                options.sort ?? "creationDate",
                options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
            )
        );

        // Store filter with pagination info for export (LegacyExportTask needs it)
        // But use a page(1, pageSize) so it starts from the beginning
        this.currentFilter = buildQuery<AccountStatementsAttributes>(
            seed(filterWithoutPagination),
            page(1, options.limitPerPage ?? 100)
        );
        this.currentFilterData = { search: options.search };

        // Build query with pagination for current request
        const query = buildQuery<AccountStatementsAttributes>(
            seed(filterWithoutPagination),
            page(options.currentPage ?? 1, options.limitPerPage ?? 100)
        );

        const API_URL: string = apiUrl + `api/${moduleName}/withCount`;
        return this.http.post<IPaginationResult<AccountStatementsAttributes>>(API_URL, {
            filter: query
        });
    }

    importFileBills(data: FormData){
        const API_URL: string = apiUrl + `api/${moduleName}/bills_banks/upload`;
        return this.http.post(API_URL, data);
    }

    importFileNegotiable(data: FormData){
        const API_URL: string = apiUrl + `api/${moduleName}/negotiable_bills/upload`;
        return this.http.post(API_URL, data);
    }

    /**
     * * 📍 Used in:
     * * - Reportes/Finanzas/Partidas abiertas (`open-item`)
     */
    findByFilter(filter: CombinedFilter<AccountStatementsAttributes>, options?: RequestOptions) {
        const API_URL: string =  apiUrl + `api/${moduleName}/withCount`;
        const headers: Record<string, any> = {};
        // see order-customer-export for more info about this header
        // ctx: stands for context
        if (options?.contextHeader) headers['x-ave-ctx'] = options.contextHeader;
        return this.http.post<IPaginationResult<AccountStatementsAttributes>>(API_URL,
            { filter: filter },
            { headers }
        );
    }

    /**
     * Método wrapper para exports que maneja la llamada HTTP con el query proporcionado
     */
    filterForExport(exportQuery: CombinedFilter<AccountStatementsAttributes>): Observable<IPaginationResult<AccountStatementsAttributes>> {
        const API_URL = `${apiUrl}api/${moduleName}/withCount`;
        return this.http.post<IPaginationResult<AccountStatementsAttributes>>(API_URL, {
            filter: exportQuery
        }, {
            headers: { 'x-ave-ctx': 'export' }
        });
    }

    // Partidas abiertas de un socio: lo consume el modal de Generación de Documentos
    findByBusinessPartner(
        businessPartnerMongoId: string | number,
        options: IPagination<AccountStatementsAttributes>,
        dataToFilter: IFilters
    ): Observable<IBusinessPartnerAccountStatementsResult<AccountStatementsAttributes>> {
        const searchQueries = buildSearchQueries<AccountStatementsAttributes>({
            search: options.search,
            searchFields: [
                'businessPartner.name',
                'businessPartner.externalKeys.code',
                'additionalOC',
                'document',
                'codeReason'
            ],
            baseFilters: [
                ['_deleted', Ops.eq(false)],
                dataToFilter.subRouteId?.length > 0
                    ? ['subRoutes.id', Ops.in(...dataToFilter.subRouteId)]
                    : null,
                dataToFilter.salesOfficeId?.length > 0
                    ? ['salesOffice.id', Ops.in(...dataToFilter.salesOfficeId)]
                    : null,
                dataToFilter.paymentConditionId?.length > 0
                    ? ['paymentConditionShort.id', Ops.in(...dataToFilter.paymentConditionId)]
                    : null,
                dataToFilter.startDate && dataToFilter.endDate
                    ? ['creationDate', Ops.btwnDates(dataToFilter.startDate, dataToFilter.endDate)]
                    : null,
                dataToFilter.expirationStartDate && dataToFilter.expirationEndDate
                    ? ['expirationDate', Ops.btwnDates(dataToFilter.expirationStartDate, dataToFilter.expirationEndDate)]
                    : null,
            ]
        });

        const baseFilter = buildQuery<AccountStatementsAttributes>(
            ...searchQueries,
            orderBy(
                options.sort ?? "creationDate",
                options.sortDirection === "ASC" ? OrderVariant.Asc : OrderVariant.Desc
            )
        );

        const query = buildQuery<AccountStatementsAttributes>(
            seed(baseFilter),
            page(options.currentPage ?? 1, options.limitPerPage ?? 100)
        );

        const API_URL = `${apiUrl}api/${moduleName}/get_account_statements_by_bp`;

        return this.http.post<IBusinessPartnerAccountStatementsResult<AccountStatementsAttributes>>(
            API_URL,
            { filter: query, businessPartnerMongoId }
        );
    }

    // Mismo endpoint que findByBusinessPartner, paginado por el exportador
    findByBusinessPartnerForExport(
        businessPartnerMongoId: string | number,
        options: IPagination<AccountStatementsAttributes>,
        dataToFilter: IFilters,
        currentPage: number,
        pageSize: number
    ): Observable<IBusinessPartnerAccountStatementsResult<AccountStatementsAttributes>> {
        const searchQueries = buildSearchQueries<AccountStatementsAttributes>({
            search: options.search,
            searchFields: [
                'businessPartner.name',
                'businessPartner.externalKeys.code',
                'additionalOC',
                'document',
                'codeReason'
            ],
            baseFilters: [
                ['_deleted', Ops.eq(false)],
                dataToFilter.subRouteId?.length > 0
                    ? ['subRoutes.id', Ops.in(...dataToFilter.subRouteId)]
                    : null,
                dataToFilter.salesOfficeId?.length > 0
                    ? ['salesOffice.id', Ops.in(...dataToFilter.salesOfficeId)]
                    : null,
                dataToFilter.paymentConditionId?.length > 0
                    ? ['paymentConditionShort.id', Ops.in(...dataToFilter.paymentConditionId)]
                    : null,
                dataToFilter.startDate && dataToFilter.endDate
                    ? ['creationDate', Ops.btwnDates(dataToFilter.startDate, dataToFilter.endDate)]
                    : null,
                dataToFilter.expirationStartDate && dataToFilter.expirationEndDate
                    ? ['expirationDate', Ops.btwnDates(dataToFilter.expirationStartDate, dataToFilter.expirationEndDate)]
                    : null,
            ]
        });

        const baseFilter = buildQuery<AccountStatementsAttributes>(
            ...searchQueries,
            orderBy(
                options.sort ?? "creationDate",
                options.sortDirection === "ASC" ? OrderVariant.Asc : OrderVariant.Desc
            )
        );

        const query = buildQuery<AccountStatementsAttributes>(
            seed(baseFilter),
            page(currentPage, pageSize)
        );

        const API_URL = `${apiUrl}api/${moduleName}/get_account_statements_by_bp`;

        return this.http.post<IBusinessPartnerAccountStatementsResult<AccountStatementsAttributes>>(
            API_URL,
            { filter: query, businessPartnerMongoId },
            { headers: { 'x-ave-ctx': 'export' } }
        );
    }

    downloadAccountStatementsPdf(
        businessPartnerMongoId: string | number,
        options: IPagination<AccountStatementsAttributes>,
        dataToFilter: IFilters
    ): Observable<void> {
        const searchQueries = buildSearchQueries<AccountStatementsAttributes>({
            search: options.search,
            searchFields: [
                'businessPartner.name',
                'businessPartner.externalKeys.code',
                'additionalOC',
                'document',
                'codeReason'
            ],
            baseFilters: [
                ['_deleted', Ops.eq(false)],
                dataToFilter.subRouteId?.length > 0
                    ? ['subRoutes.id', Ops.in(...dataToFilter.subRouteId)]
                    : null,
                dataToFilter.salesOfficeId?.length > 0
                    ? ['salesOffice.id', Ops.in(...dataToFilter.salesOfficeId)]
                    : null,
                dataToFilter.paymentConditionId?.length > 0
                    ? ['paymentConditionShort.id', Ops.in(...dataToFilter.paymentConditionId)]
                    : null,
                dataToFilter.startDate && dataToFilter.endDate
                    ? ['creationDate', Ops.btwnDates(dataToFilter.startDate, dataToFilter.endDate)]
                    : null,
                dataToFilter.expirationStartDate && dataToFilter.expirationEndDate
                    ? ['expirationDate', Ops.btwnDates(dataToFilter.expirationStartDate, dataToFilter.expirationEndDate)]
                    : null,
            ]
        });

        const filterQuery = buildQuery<AccountStatementsAttributes>(
            ...searchQueries,
            orderBy("creationDate", OrderVariant.Desc),
        );

        const params = new HttpParams()
            .set('format', 'letter')
            .set('landscape', 'false')
            .set('scale', '1')
            .set('printBackground', 'true')
            .set('marginTop', '10mm')
            .set('marginBottom', '10mm')
            .set('marginLeft', '10mm')
            .set('marginRight', '10mm');

        const API_URL = `${env.API_DOCUMENT_EXPORT_URL}api/account-statements-pdf`;

        return this.http.post(API_URL, {
            filter: filterQuery,
            businessPartnerMongoId
        }, {
            params,
            responseType: 'blob'
        }).pipe(
            tap((blob: Blob) => saveAs(blob, this.generateFileName("Partidas_Abiertas"))),
            map(() => undefined)
        );
    }

    private generateFileName(docType: string): string {
        const date = new Date().toISOString().split('T')[0];
        return `${docType}_${date}.pdf`;
    }
}
